import React, { useState, useEffect, useRef } from 'react';
import {
  getUsersByOrganization,
  getOrganizationMembersPaginated,
  getUsersByRole,
  updateUserRole,
  getTeamsByOrganization,
  addUserToTeam,
  removeUserFromTeam,
  deleteUser,
  updateUserData,
  toggleUserStatus,
  getAgenturenByOrganization,
  createAgentur,
  createUser,
  createTeam,
  getTeamsByAgentur,
  deleteATeam,
  updateTeam
} from '../../api';
import CreateUser from '../CreateUser';
import EditUserDialog from '../../components/common/EditUserDialog';
import MemberCardForm from './MemberCardForm';
import { useNavigate} from 'react-router-dom';

function OrganizationMembersManagement({ user, organizationId, onBack }) {
  const [members, setMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]); // Ungefilterte Mitglieder
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [agenturen, setAgenturen] = useState([]);
  
  // Neue States für Agentur-Erstellung
  const [showCreateAgenturModal, setShowCreateAgenturModal] = useState(false);
  const [agenturName, setAgenturName] = useState('');
  const [agenturMembers, setAgenturMembers] = useState([]);
  const [showMemberCardForm, setShowMemberCardForm] = useState(false);
  const [editingMemberIndex, setEditingMemberIndex] = useState(null);
  const [useMasterPassword, setUseMasterPassword] = useState(false);
  const [masterPassword, setMasterPassword] = useState('');
  
  // NEU: States für Teams-Verwaltung
  const [agenturTeams, setAgenturTeams] = useState([]);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', beschreibung: '' });
  const [editingTeamIndex, setEditingTeamIndex] = useState(null);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    sortBy: 'full_name', // full_name, team_id, created_at
    sortOrder: 'asc', // asc, desc
    teamFilter: '', // Team-Filter
    agenturFilter: '', // NEU: Agentur-Filter
  });
  
  // Neue States für Suche und Pagination
  const [searchName, setSearchName] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 25,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Scrollbar-Refs und Synchronisation
  const topScrollRef = useRef(null); // Dies wird die sichtbare Scrollbar sein
  const tableContentRef = useRef(null); // Dies ist der Container, der den Tabelleninhalt scrollt
  
  // Touch-Events für horizontales Scrollen
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Touch-Event-Handler
  const handleTouchStart = (e) => {
    setTouchEnd(null); // Reset touch end
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe || isRightSwipe) {
      const tableContent = tableContentRef.current;
      if (tableContent) {
        const scrollAmount = 200; // Scroll-Betrag in Pixeln
        if (isLeftSwipe) {
          // Nach links wischen = nach rechts scrollen
          tableContent.scrollLeft += scrollAmount;
        } else {
          // Nach rechts wischen = nach links scrollen
          tableContent.scrollLeft -= scrollAmount;
        }
      }
    }
  };

  useEffect(() => {
    const topScroll = topScrollRef.current;
    const tableContent = tableContentRef.current;

    if (!topScroll || !tableContent) return;

    // Funktion zum Anpassen der Breite des Platzhalters in der oberen Scrollbar
    // Dies stellt sicher, dass die obere Scrollbar die gleiche Scrollbreite hat wie die Tabelle
    const adjustTopScrollWidth = () => {
      if (tableContent.scrollWidth > tableContent.clientWidth) {
        // Nur anpassen, wenn die Tabelle scrollbar ist
        topScroll.firstChild.style.width = `${tableContent.scrollWidth}px`;
      } else {
        topScroll.firstChild.style.width = '100%'; // Wenn nicht scrollbar, volle Breite
      }
    };

    // Beim Laden und bei jeder Größenänderung anpassen
    adjustTopScrollWidth();
    window.addEventListener('resize', adjustTopScrollWidth);

    const handleTopScroll = () => {
      tableContent.scrollLeft = topScroll.scrollLeft;
    };

    const handleTableScroll = () => {
      topScroll.scrollLeft = tableContent.scrollLeft;
    };

    topScroll.addEventListener('scroll', handleTopScroll);
    tableContent.addEventListener('scroll', handleTableScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableContent.removeEventListener('scroll', handleTableScroll);
      window.removeEventListener('resize', adjustTopScrollWidth);
    };
  }, [members]); // Abhängigkeit von 'members' hinzufügen, damit Breite bei Datenänderung angepasst wird


  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      sortBy: 'full_name',
      sortOrder: 'asc',
      teamFilter: '',
      agenturFilter: '', // NEU
    });
  };

  const currentOrganizationId = organizationId;

  const isAdmin = user?.isAdmin || false;
  const isOrgLeader = user?.isOrgLeader || false;

  const loadAllMembers = async (page = pagination.currentPage, limit = pagination.limit) => {
    setLoading(true);
    setError('');

    if (!currentOrganizationId) {
      setError('Organisations-ID fehlt - Bitte kontaktieren Sie den Administrator');
      setLoading(false);
      return;
    }

    try {
      console.log("currentOrganizationId : ", currentOrganizationId);
      const response = await getOrganizationMembersPaginated(user.id, currentOrganizationId, {
        searchName: searchName,
        page: page,
        perPage: limit,
        filters: filters
      });

      if (response.success) {
        setAllMembers(response.users);
        if (response.pagination) {
          // Konvertiere die Pagination-Daten in das neue Format
          setPagination({
            currentPage: response.pagination.page || page,
            totalPages: response.pagination.totalPages || 1,
            totalCount: response.pagination.total || 0,
            limit: limit,
            hasNextPage: response.pagination.page < response.pagination.totalPages,
            hasPrevPage: response.pagination.page > 1
          });
        }
      } else {
        setError(response.error || 'Fehler beim Laden der Mitglieder');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter werden jetzt serverseitig angewendet, daher keine clientseitige Filterung mehr nötig

  const loadTeams = async () => {
    if (!currentOrganizationId) {
      return;
    }

    try {
      const response = await getTeamsByOrganization(user.id, currentOrganizationId);

      if (response.success) {
        setTeams(response.teams);
      } else {
        setError(response.error || 'Fehler beim Laden der Teams');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const loadAgenturen = async () => {
    if (!currentOrganizationId) return;
    try {
      console.log("Leonie");
      const response = await getAgenturenByOrganization(currentOrganizationId);
      if (response.success) {
        setAgenturen(response.agenturen);
      } else {
        setError(response.error || 'Fehler beim Laden der Agenturen');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  // Pagination-Handler
  const handlePageChange = (newPage) => {
    loadAllMembers(newPage, pagination.limit);
  };

  const handleLimitChange = (newLimit) => {
    loadAllMembers(1, newLimit);
  };

  useEffect(() => {
    if (currentOrganizationId) {
      loadAllMembers(1, pagination.limit); // Immer mit Seite 1 starten
      loadTeams();
      loadAgenturen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId]);

  // Suche und Pagination neu laden, wenn sich Suchparameter ändern (mit Debouncing)
  useEffect(() => {
    if (!currentOrganizationId) return;
    
    // Debouncing: Warte 500ms nach dem letzten Tastendruck
    const timeoutId = setTimeout(() => {
      loadAllMembers(1, pagination.limit);
    }, 500);
    
    // Cleanup: Lösche den Timeout wenn sich searchName erneut ändert
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchName]);

  // Filter-Änderungen: Zurück zur ersten Seite und neu laden
  useEffect(() => {
    if (currentOrganizationId) {
      loadAllMembers(1, pagination.limit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    // Filter werden jetzt serverseitig angewendet, daher keine clientseitige Filterung mehr nötig
    // Aber wir können die angezeigten Mitglieder direkt setzen
    setMembers(allMembers);
  }, [allMembers]);

  const getRoleName = (roleType) => {
    switch (parseInt(roleType, 10)) {
      case 1: return 'Administrator';
      case 2: return 'Teamleiter';
      case 3: return 'Organisationsleiter';
      case 4: return 'Callcenter Leader';
      case 5: return 'Callcenter Mitarbeiter';
      case 6: return 'Fachberater';
      case 7: return 'Agentur Leiter';
      default: return 'Benutzer';
    }
  };

  const getTeamName = (teamId) => {
    if (!teamId) return 'Kein Team';
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unbekanntes Team';
  };

  const getAgenturName = (agenturId) => {
    console.log("Agenturen : ", agenturen);
    if (!agenturId) return 'Keine Agentur';
    const agentur = agenturen.find(a => a.id === agenturId);
    return agentur ? agentur.name : 'Unbekannte Agentur';
  };

  const handleRemoveFromTeam = async (memberId, teamId) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich aus dem Team entfernen?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await removeUserFromTeam(user.id, memberId, teamId);

      if (response.success) {
        loadAllMembers();
      } else {
        setError(response.error || 'Fehler beim Entfernen aus dem Team');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToTeam = async (memberId, teamId) => {
    setLoading(true);
    try {
      const response = await addUserToTeam(user.id, memberId, teamId);

      if (response.success) {
        loadAllMembers();
      } else {
        setError(response.error || 'Fehler beim Hinzufügen zum Team');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (memberId, newRoleType) => {
    if (isOrgLeader && (newRoleType === 1 || newRoleType === 3)) {
      setError('Organisationsleiter können keine Administratoren oder andere Organisationsleiter erstellen');
      return;
    }

    setLoading(true);
    try {
      const response = await updateUserRole(user.id, memberId, newRoleType);

      if (response.success) {
        loadAllMembers();
      } else {
        setError(response.error || 'Fehler beim Aktualisieren der Rolle');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (memberId) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await deleteUser(user.id, memberId);

      if (response.success) {
        loadAllMembers();
      } else {
        setError(response.error || 'Fehler beim Löschen des Benutzers');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeTeam = async (memberId, newTeamId) => {
    setLoading(true);
    try {
      const member = allMembers.find(m => m.id === memberId);
      if (member && member.team_id) {
        await removeUserFromTeam(user.id, memberId, member.team_id);
      }

      if (newTeamId) {
        await addUserToTeam(user.id, memberId, newTeamId);
      }

      loadAllMembers();
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMember = (response) => {
    if (response.success) {
      setShowAddMemberForm(false);
      loadAllMembers();
    }
  };

  const handleEditUser = (member) => {
    setSelectedUserForEdit(member);
    setShowEditUserDialog(true);
  };

  const handleEditUserSuccess = () => {
    setShowEditUserDialog(false);
    setSelectedUserForEdit(null);
    loadAllMembers();
  };

  const handleEditUserCancel = () => {
    setShowEditUserDialog(false);
    setSelectedUserForEdit(null);
  };

  // Agentur mit Mitgliedern erstellen
  const handleCreateAgenturWithMembers = async () => {
    if (!agenturName || agenturMembers.length === 0) {
      setError('Bitte geben Sie einen Agentur-Namen ein und fügen Sie mindestens ein Mitglied hinzu.');
      return;
    }

    if (useMasterPassword && !masterPassword) {
      setError('Bitte geben Sie ein Master-Passwort ein.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Erstelle Agentur:', agenturName);
      console.log('Mitglieder:', agenturMembers);
      console.log('Master-Password verwendet:', useMasterPassword);
      
      // 1. Agentur erstellen
      const agenturResponse = await createAgentur(agenturName, '', user.organization_id);
      
      console.log('Agentur API Response:', agenturResponse);
      
      if (!agenturResponse) {
        throw new Error('Keine Antwort vom Server beim Erstellen der Agentur');
      }
      
      if (!agenturResponse.success) {
        throw new Error(agenturResponse.error || 'Fehler beim Erstellen der Agentur');
      }
      
      // Verschiedene mögliche Response-Strukturen berücksichtigen
      let agenturId;
      console.log('Versuche Agentur-ID zu extrahieren...');
      console.log('agenturResponse.agentur:', agenturResponse.agentur);
      console.log('agenturResponse.id:', agenturResponse.id);
      console.log('agenturResponse.data:', agenturResponse.data);
      
      if (agenturResponse.agentur && agenturResponse.agentur.id) {
        agenturId = agenturResponse.agentur.id;
        console.log('ID gefunden in agenturResponse.agentur.id:', agenturId);
      } else if (agenturResponse.id) {
        agenturId = agenturResponse.id;
        console.log('ID gefunden in agenturResponse.id:', agenturId);
      } else if (agenturResponse.data && agenturResponse.data.id) {
        agenturId = agenturResponse.data.id;
        console.log('ID gefunden in agenturResponse.data.id:', agenturId);
      } else {
        // Versuche alle möglichen Felder zu durchsuchen
        console.log('Durchsuche alle Felder nach ID...');
        for (const key in agenturResponse) {
          console.log(`${key}:`, agenturResponse[key]);
          if (typeof agenturResponse[key] === 'object' && agenturResponse[key] && agenturResponse[key].id) {
            agenturId = agenturResponse[key].id;
            console.log(`ID gefunden in ${key}.id:`, agenturId);
            break;
          }
        }
        
        if (!agenturId) {
          console.error('Unerwartete Agentur-Response-Struktur:', agenturResponse);
          throw new Error('Agentur wurde erstellt, aber ID konnte nicht ermittelt werden. Response: ' + JSON.stringify(agenturResponse));
        }
      }
      
      console.log('Agentur erfolgreich erstellt mit ID:', agenturId);
      
      // 2. Teams erstellen (falls vorhanden)
      const createdTeams = [];
      const teamIdMap = new Map(); // Mapping von Index zu echter Team-ID
      
      if (agenturTeams.length > 0) {
        console.log('Erstelle Teams:', agenturTeams);
        
        for (let i = 0; i < agenturTeams.length; i++) {
          const team = agenturTeams[i];
          try {
            const teamResult = await createTeam({
              userId: user.id,
              agenturId: agenturId,
              name: team.name,
              beschreibung: team.beschreibung
            });
            
            if (teamResult.success) {
              createdTeams.push(team);
              teamIdMap.set(i, teamResult.teamId); // Speichere die echte Team-ID
              console.log('Team erfolgreich erstellt:', teamResult);
            } else {
              console.error('Fehler beim Erstellen des Teams:', teamResult.error);
            }
          } catch (teamErr) {
            console.error('Fehler beim Erstellen des Teams:', teamErr);
          }
        }
      }
      
      // 3. Mitglieder erstellen und zur Agentur hinzufügen
      const createdMembers = [];
      const failedMembers = [];
      
      for (const member of agenturMembers) {
        try {
          const password = useMasterPassword ? masterPassword : member.password;
          
          // Team-ID konvertieren (von Index zu echter ID)
          let finalATeamId = null;
          if (member.a_team_id !== '' && member.a_team_id !== null && member.a_team_id !== undefined) {
            const teamIndex = parseInt(member.a_team_id);
            if (teamIdMap.has(teamIndex)) {
              finalATeamId = teamIdMap.get(teamIndex);
            }
          }
          
          const memberData = {
            ...member,
            agenturId: agenturId,
            a_team_id: finalATeamId, // Verwende die echte Team-ID
            password: password,
            organizationId: user.organization_id,
            // Passwort-Bestätigung entfernen
            confirmPassword: undefined
          };
          
          console.log('Erstelle Mitglied:', memberData);
          
          const memberResponse = await createUser(memberData, user.id);
          
          if (memberResponse.success) {
            createdMembers.push(member);
            console.log('Mitglied erfolgreich erstellt:', memberResponse);
          } else {
            failedMembers.push({ member, error: memberResponse.error });
            console.error('Fehler beim Erstellen des Mitglieds:', memberResponse);
          }
        } catch (memberErr) {
          failedMembers.push({ member, error: memberErr.message });
          console.error('Fehler beim Erstellen des Mitglieds:', memberErr);
        }
      }
      
      // 3. Ergebnis anzeigen
      if (failedMembers.length === 0) {
        // Alle erfolgreich erstellt
        setShowCreateAgenturModal(false);
        setAgenturName('');
        setAgenturMembers([]);
        setUseMasterPassword(false);
        setMasterPassword('');
        
        // Liste neu laden
        loadAllMembers();
        
        const teamsMessage = createdTeams.length > 0 ? `, ${createdTeams.length} Teams` : '';

      } else if (createdMembers.length > 0) {
        // Teilweise erfolgreich
        setError(`Agentur wurde erstellt, aber ${failedMembers.length} von ${agenturMembers.length} Mitgliedern konnten nicht erstellt werden. Details: ${failedMembers.map(f => f.error).join(', ')}`);
        
        // Modal offen lassen, damit Benutzer die Fehler sehen kann
      } else {
        // Keine Mitglieder erfolgreich erstellt
        setError(`Agentur wurde erstellt, aber keine Mitglieder konnten erstellt werden. Fehler: ${failedMembers.map(f => f.error).join(', ')}`);
      }
      
    } catch (err) {
      setError('Fehler beim Erstellen der Agentur und Mitglieder: ' + err.message);
      console.error('Fehler beim Erstellen der Agentur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (member) => {
    if (!window.confirm(`Möchten Sie ${member.full_name} wirklich ${member.active === 1 ? 'deaktivieren' : 'aktivieren'}?`)) {
      return;
    }

    setLoading(true);
    try {
      const newActiveStatus = member.active === 1 ? 0 : 1;
      const response = await toggleUserStatus(user.id, member.id, newActiveStatus);

      if (response.success) {
        loadAllMembers();
      } else {
        setError(response.error || 'Fehler beim Umschalten des Benutzerstatus');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // NEU: Team-Verwaltungsfunktionen
  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      setError('Bitte einen Teamnamen angeben.');
      return;
    }

    const teamData = {
      name: newTeam.name.trim(),
      beschreibung: newTeam.beschreibung.trim()
    };

    setAgenturTeams(prev => [...prev, teamData]);
    setNewTeam({ name: '', beschreibung: '' });
    setShowTeamForm(false);
  };

  const handleEditTeam = (index) => {
    const team = agenturTeams[index];
    setNewTeam({ name: team.name, beschreibung: team.beschreibung });
    setEditingTeamIndex(index);
    setShowTeamForm(true);
  };

  const handleUpdateTeam = async () => {
    if (!newTeam.name.trim()) {
      setError('Bitte einen Teamnamen angeben.');
      return;
    }

    const updatedTeam = {
      name: newTeam.name.trim(),
      beschreibung: newTeam.beschreibung.trim()
    };

    setAgenturTeams(prev => 
      prev.map((team, index) => 
        index === editingTeamIndex ? updatedTeam : team
      )
    );
    
    setNewTeam({ name: '', beschreibung: '' });
    setEditingTeamIndex(null);
    setShowTeamForm(false);
  };

  const handleDeleteTeam = (index) => {
    if (window.confirm('Möchten Sie dieses Team wirklich löschen?')) {
      setAgenturTeams(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleTeamFormCancel = () => {
    setShowTeamForm(false);
    setEditingTeamIndex(null);
    setNewTeam({ name: '', beschreibung: '' });
  };

  if (!currentOrganizationId) {
    return (
      <div className="organization-members-management">
        <div className="page-header-wrapper">
          <button className="back-button" onClick={() => navigate("/agenturen")}>
            ← Zurück
          </button>
          <h2>Mitglieder verwalten</h2>
        </div>

        <div className="error-message">
          Sie gehören keiner Organisation an. Bitte kontaktieren Sie den Administrator, um einer Organisation zugewiesen zu werden.
        </div>
      </div>
    );
  }

  console.log("members : ", members);
  
  return (
    <div className="organization-members-management">
      <div className="page-header-wrapper">
        <button className="back-button" onClick={() => navigate("/agenturen")}>
          ← Zurück
        </button>
        <h2>Mitglieder verwalten</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading && allMembers.length === 0 ? (
        <div className="loading">Mitglieder werden geladen...</div>
      ) : (
        <>
          <div className="members-actions" style={{ display: 'flex' }}>
            <button
              className="action-button"
              onClick={() => setShowAddMemberForm(true)}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              + Neues Mitglied
            </button>
            <button
              className="action-button"
              onClick={() => setShowCreateAgenturModal(true)}
              style={{
                flex: 1,
                marginLeft: '10px',
                padding: '10px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              + Neue Agentur & Mitglieder
            </button>
          </div>


          <div className="leads-filters">
            <div className="filter-row">
              {/* Suchfeld */}
              <div className="filter-group" style={{ flex: '1', minWidth: '200px' }}>
                <label>Nach Name suchen:</label>
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Name oder E-Mail eingeben..."
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div className="filter-group">
                <label>Sortieren nach:</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                >
                  <option value="full_name">Name</option>
                  <option value="team_id">Team</option>
                  <option value="created_at">Erstellungsdatum</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Reihenfolge:</label>
                <select
                  value={filters.sortOrder}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                >
                  <option value="asc">Aufsteigend</option>
                  <option value="desc">Absteigend</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Projekt:</label>
                <select
                  value={filters.teamFilter}
                  onChange={(e) => handleFilterChange('teamFilter', e.target.value)}
                >
                  <option value="">Alle Teams</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Agentur:</label>
                <select
                  value={filters.agenturFilter}
                  onChange={(e) => handleFilterChange('agenturFilter', e.target.value)}
                >
                  <option value="">Alle Agenturen</option>
                  <option value="none">Keine Agentur</option>
                  {agenturen.map(agentur => (
                    <option key={agentur.id} value={agentur.id}>{agentur.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <button className="reset-filters-button" onClick={() => {
                  resetFilters();
                  setSearchName('');
                  loadAllMembers(1, pagination.limit);
                }}>
                  Filter zurücksetzen
                </button>
              </div>
            </div>
          </div>

          {/* Pagination Controls - Über der Tabelle */}
          {!loading && members.length > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                <span>Seite {pagination.currentPage} von {pagination.totalPages}</span>
                <span>•</span>
                <span>{pagination.totalCount} Mitglieder insgesamt</span>
              </div>
              
              <div className="pagination-controls">
                <button 
                  className="pagination-btn" 
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                >
                  ← Zurück
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`page-number ${pagination.currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button 
                  className="pagination-btn" 
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                >
                  Weiter →
                </button>
              </div>
              
              <div className="members-per-page">
                <label>Mitglieder pro Seite:</label>
                <select 
                  value={pagination.limit} 
                  onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={150}>150</option>
                  <option value={200}>200</option>
                </select>
              </div>
            </div>
          )}

          {/* Obere Scrollbar */}
          <div
            ref={topScrollRef}
            className="top-scrollbar-container"
          >
            {/* Dieser Div wird die Breite der Tabelle simulieren und die Scrollbar erzeugen */}
            <div className="top-scrollbar-content-placeholder"></div>
          </div>

          {/* Haupttabellen-Container, der den Inhalt der Tabelle hält */}
          <div
            ref={tableContentRef}
            className="main-table-content-container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <table className="members-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Rolle</th>
                  <th>Projekt</th>
                  <th>Agentur</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-data">
                      {filters.teamFilter ?
                        'Keine Mitglieder mit den aktuellen Filtern gefunden' :
                        'Keine Mitglieder gefunden'
                      }
                    </td>
                  </tr>
                ) : (
                  members.map(member => (
                    <tr key={member.id}>
                      <td>{member.full_name || 'Kein Name'}</td>
                      <td>{member.email}</td>
                      <td>
                        {getRoleName(member.role_type)}
                        {(parseInt(member.role_type, 10) !== 1 && parseInt(member.role_type, 10) !== 3) && (
                          <div className="role-selector">
                            <select
                              defaultValue=""
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value) {
                                  handleUpdateRole(member.id, parseInt(value, 10));
                                  e.target.value = "";
                                }
                              }}
                            >
                              <option value="">Rolle ändern</option>
                              <option value="0">Benutzer</option>
                              <option value="2">Teamleiter</option>
                              <option value="6">Fachberater</option>
                              <option value="7">Agentur Leiter</option>
                            </select>
                          </div>
                        )}
                      </td>
                      <td>
                        {getTeamName(member.team_id)}

                        <div className="team-selector">
                          <select
                            defaultValue=""
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value) {
                                handleChangeTeam(member.id, value === "none" ? null : value);
                                e.target.value = "";
                              }
                            }}
                          >
                            <option value="">Team ändern</option>
                            <option value="none">Kein Team</option>
                            {teams.map(team => (
                              <option key={team.id} value={team.id}>
                                {team.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td>{getAgenturName(member.agentur_id)}</td>
                      <td>
                        <span className={`status-badge ${member.active === 1 ? 'active' : 'inactive'}`}>
                          {member.active === 1 ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td>
                        <div className="member-actions">
                          {(isAdmin || isOrgLeader) && (
                            <button
                              className="edit-user-button"
                              onClick={() => handleEditUser(member)}
                              title="Benutzer bearbeiten"
                            >
                              Bearbeiten
                            </button>
                          )}
                          {(isAdmin || isOrgLeader) && member.id !== user.id && (
                            <button
                              className={`toggle-status-button ${member.active === 1 ? 'deactivate' : 'activate'}`}
                              onClick={() => handleToggleUserStatus(member)}
                              title={member.active === 1 ? 'Benutzer deaktivieren' : 'Benutzer aktivieren'}
                            >
                              {member.active === 1 ? 'Deaktivieren' : 'Aktivieren'}
                            </button>
                          )}
                          {isAdmin && member.id !== user.id && (
                            <button
                              className="delete-member-button"
                              onClick={() => handleDeleteUser(member.id)}
                            >
                              Löschen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        </>
      )}

      {showAddMemberForm && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <h3>Neues Mitglied hinzufügen</h3>
            <CreateUser
              user={user}
              onClose={() => {
                setShowAddMemberForm(false);
                loadAllMembers();
              }}
              showAgenturSelection={true}
            />
          </div>
        </div>
      )}

      {showEditUserDialog && selectedUserForEdit && (
        <EditUserDialog
          user={user}
          targetUser={selectedUserForEdit}
          onSave={handleEditUserSuccess}
          onCancel={handleEditUserCancel}
          isMobile={false}
          isTablet={false}
        />
      )}

      <style jsx>{`
        .page-header-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .page-header-wrapper h2 {
          margin: 0;
          color: #2d3748;
          font-size: 1.5rem;
        }

        .page-header-wrapper .back-button {
          padding: 8px 16px;
          background: #4a6de5 !important;
          color: white !important;
          border: none !important;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
          font-weight: 500;
        }

        .page-header-wrapper .back-button:hover {
          background: #3c5ce0 !important;
        }

        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          margin-top: 20px;
          margin-bottom: 20px;
        }

        .pagination-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
          color: #4a5568;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .pagination-btn {
          padding: 8px 16px;
          background: #4a6de5;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .pagination-btn:hover:not(:disabled) {
          background: #3c5ce0;
        }

        .pagination-btn:disabled {
          background: #cbd5e0;
          cursor: not-allowed;
        }

        .page-numbers {
          display: flex;
          gap: 4px;
        }

        .page-number {
          padding: 8px 12px;
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          color: #2d3748;
          transition: all 0.2s;
        }

        .page-number:hover {
          background: #edf2f7;
          border-color: #cbd5e0;
        }

        .page-number.active {
          background: #4a6de5;
          color: white;
          border-color: #4a6de5;
        }

        .members-per-page {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .members-per-page label {
          font-size: 0.875rem;
          color: #4a5568;
        }

        .members-per-page select {
          padding: 6px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.875rem;
          background: white;
        }

        /* RESPONSIVE PAGINATION */
        @media (max-width: 768px) {
          .pagination-container {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .pagination-controls {
            order: 1;
          }

          .pagination-info {
            order: 2;
          }

          .members-per-page {
            order: 3;
          }
        }

        .status-badge {
          display: inline-block;
          padding: 0.2rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.65rem;
          font-weight: 500;
          text-align: center;
          white-space: nowrap;
          min-width: 50px;
          max-width: 90px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .status-badge.active {
          background-color: #c6f6d5;
          color: #2f855a;
        }

        .status-badge.inactive {
          background-color: #fed7d7;
          color: #c53030;
        }

        .member-actions {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .edit-user-button {
          padding: 4px 8px;
          font-size: 0.7rem;
          margin: 1px;
          border: none;
          border-radius: 3px;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
          background: #3182ce;
          color: white;
        }

        .edit-user-button:hover {
          background: #2c5aa0;
        }

        .toggle-status-button {
          padding: 4px 8px;
          font-size: 0.7rem;
          margin: 1px;
          border: none;
          border-radius: 3px;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .toggle-status-button.activate {
          background: #38a169;
          color: white;
        }

        .toggle-status-button.activate:hover {
          background: #2f855a;
        }

        .toggle-status-button.deactivate {
          background: #d69e2e;
          color: white;
        }

        .toggle-status-button.deactivate:hover {
          background: #b7791f;
        }

        .delete-member-button {
          padding: 4px 8px;
          font-size: 0.7rem;
          margin: 1px;
          border: none;
          border-radius: 3px;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
          background: #e53e3e;
          color: white;
        }

        .delete-member-button:hover {
          background: #c53030;
        }

        /* NEUE STYLES FÜR DIE OBERE SCROLLBAR */
        .top-scrollbar-container {
          overflow-x: auto; /* Dies ist der Container mit der sichtbaren Scrollbar */
          overflow-y: hidden;
          height: 15px; /* Höhe der Scrollbar */
          margin-bottom: 10px; /* Abstand zur Tabelle */
          position: sticky; /* Bleibt oben, wenn gescrollt wird */
          top: 0;
          z-index: 2; /* Überlappt andere Inhalte */
          background: #fff; /* Hintergrundfarbe für die Scrollbar-Fläche */
          border-bottom: 1px solid #e2e8f0;
        }

        /* Der Platzhalter, der die Scrollbreite der oberen Scrollbar definiert */
        .top-scrollbar-content-placeholder {
          height: 1px; /* Minimalhöhe, nur um die Scrollbreite zu setzen */
          /* Breite wird dynamisch per JavaScript gesetzt */
        }

        /* Der Container, der die eigentliche Tabelle enthält */
        .main-table-content-container {
          overflow-x: hidden; /* **WICHTIG:** Hier wird die Scrollbar ausgeblendet */
          overflow-y: auto; /* Vertikales Scrollen ist weiterhin möglich */
          width: 100%;
          max-width: 100%;
          /* Optional: Eine feste Höhe geben, wenn der Tabellenbereich selbst scrollen soll */
          /* height: 400px; */
        }

        .members-table {
          min-width: 1100px; /* Breite der Tabelle, die das Scrollen erzwingt */
          width: 100%; /* Stellt sicher, dass die Tabelle die Breite des Containers nutzt */
          border-collapse: collapse;
        }
      `}</style>

      {/* Agentur-Erstellung Modal */}
      {showCreateAgenturModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-dialog" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div className="modal-header" style={{
              display: 'flex',
              alignItems: 'center',
              padding: '20px',
              borderBottom: '1px solid #e2e8f0',
              marginBottom: '20px',
              position: 'relative'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '1.5rem', 
                fontWeight: '600',
                width: '80%',
                textAlign: 'left'
              }}>
                Neue Agentur & Mitglieder erstellen
              </h3>
              <button 
                className="close-button" 
                onClick={() => {
                  setShowCreateAgenturModal(false);
                  setAgenturName('');
                  setAgenturMembers([]);
                  setUseMasterPassword(false);
                  setMasterPassword('');
                  setAgenturTeams([]);
                  setShowTeamForm(false);
                  setEditingTeamIndex(null);
                  setNewTeam({ name: '', beschreibung: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s',
                  marginLeft: 'auto',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="modal-content" style={{ padding: '0 20px 20px 20px' }}>
              {/* Agentur-Name Eingabe */}
              <div className="form-section" style={{ marginBottom: '30px' }}>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', fontWeight: '600' }}>
                  Agentur-Informationen
                </h4>
                <div className="form-row" style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                  <div className="form-field" style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      Agentur-Name*
                    </label>
                    <input
                      type="text"
                      value={agenturName}
                      onChange={(e) => setAgenturName(e.target.value)}
                      placeholder="Name der Agentur eingeben"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* NEU: Teams-Verwaltung */}
              <div className="form-section" style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                    Teams ({agenturTeams.length})
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTeamIndex(null);
                      setNewTeam({ name: '', beschreibung: '' });
                      setShowTeamForm(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    + Team hinzufügen
                  </button>
                </div>

                {/* Teams-Karten */}
                {agenturTeams.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '2px dashed #d1d5db'
                  }}>
                    Noch keine Teams hinzugefügt. Klicken Sie auf "+ Team hinzufügen".
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {agenturTeams.map((team, index) => (
                      <div key={index} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '15px',
                        backgroundColor: '#fafafa'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <h5 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '600' }}>
                              {team.name}
                            </h5>
                            <div style={{ fontSize: '14px', color: '#6b7280' }}>
                              {team.beschreibung || 'Keine Beschreibung'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                            <button
                              onClick={() => handleEditTeam(index)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Bearbeiten
                            </button>
                            <button
                              onClick={() => handleDeleteTeam(index)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Löschen
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mitglieder-Liste */}
              <div className="form-section" style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                    Mitglieder ({agenturMembers.length})
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingMemberIndex(null);
                      setShowMemberCardForm(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    + Mitglied hinzufügen
                  </button>
                </div>

                {/* Mitglieder-Karten */}
                {agenturMembers.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '2px dashed #d1d5db'
                  }}>
                    Noch keine Mitglieder hinzugefügt. Klicken Sie auf "+ Mitglied hinzufügen".
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {agenturMembers.map((member, index) => (
                      <div key={index} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '15px',
                        backgroundColor: '#fafafa'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <h5 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '600' }}>
                              {member.fullName || 'Unbenanntes Mitglied'}
                            </h5>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '14px' }}>
                              <div><strong>E-Mail:</strong> {member.email}</div>
                              <div><strong>Rolle:</strong> {getRoleName(member.roleType)}</div>
                              <div><strong>Team:</strong> {member.teamId ? getTeamName(member.teamId) : 'Kein Team'}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                            <button
                              onClick={() => {
                                setEditingMemberIndex(index);
                                setShowMemberCardForm(true);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Bearbeiten
                            </button>
                            <button
                              onClick={() => {
                                const newMembers = agenturMembers.filter((_, i) => i !== index);
                                setAgenturMembers(newMembers);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Löschen
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Master-Password Option */}
              <div className="form-section" style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <input
                    type="checkbox"
                    id="useMasterPassword"
                    checked={useMasterPassword}
                    onChange={(e) => setUseMasterPassword(e.target.checked)}
                  />
                  <label htmlFor="useMasterPassword" style={{ fontWeight: '500' }}>
                    Gleiches Passwort für alle Mitglieder verwenden
                  </label>
                </div>
                {useMasterPassword && (
                  <div className="form-field" style={{ maxWidth: '300px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      Master-Passwort*
                    </label>
                    <input
                      type="password"
                      value={masterPassword}
                      onChange={(e) => setMasterPassword(e.target.value)}
                      placeholder="Passwort für alle Mitglieder"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      required={useMasterPassword}
                    />
                  </div>
                )}
              </div>

              {/* Aktionen */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '30px',
                paddingTop: '20px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateAgenturModal(false);
                    setAgenturName('');
                    setAgenturMembers([]);
                    setUseMasterPassword(false);
                    setMasterPassword('');
                    setAgenturTeams([]);
                    setShowTeamForm(false);
                    setEditingTeamIndex(null);
                    setNewTeam({ name: '', beschreibung: '' });
                  }}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#374151',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleCreateAgenturWithMembers}
                  disabled={!agenturName || agenturMembers.length === 0 || (useMasterPassword && !masterPassword)}
                  style={{
                    padding: '10px 20px',
                    background: agenturMembers.length > 0 && agenturName ? '#059669' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: agenturMembers.length > 0 && agenturName ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Agentur & Mitglieder erstellen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team-Form */}
      {showTeamForm && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div className="modal-dialog" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            width: '90%',
            maxWidth: '500px',
            padding: '20px'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '1.2rem', fontWeight: '600' }}>
              {editingTeamIndex !== null ? 'Team bearbeiten' : 'Neues Team erstellen'}
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Teamname *
              </label>
              <input
                type="text"
                value={newTeam.name}
                onChange={(e) => setNewTeam(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Name des Teams"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                required
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Beschreibung
              </label>
              <textarea
                value={newTeam.beschreibung}
                onChange={(e) => setNewTeam(prev => ({ ...prev, beschreibung: e.target.value }))}
                placeholder="Beschreibung (optional)"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={handleTeamFormCancel}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={editingTeamIndex !== null ? handleUpdateTeam : handleCreateTeam}
                disabled={!newTeam.name.trim()}
                style={{
                  padding: '8px 16px',
                  background: newTeam.name.trim() ? '#8b5cf6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: newTeam.name.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {editingTeamIndex !== null ? 'Aktualisieren' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mitglieder-Karten Form */}
      {showMemberCardForm && (
        <MemberCardForm
          member={editingMemberIndex !== null ? agenturMembers[editingMemberIndex] : null}
          teams={teams}
          passedAgenturTeams={agenturTeams}
          onSave={(memberData) => {
            if (editingMemberIndex !== null) {
              // Bearbeiten
              const newMembers = [...agenturMembers];
              newMembers[editingMemberIndex] = memberData;
              setAgenturMembers(newMembers);
            } else {
              // Neu hinzufügen
              setAgenturMembers([...agenturMembers, memberData]);
            }
            setShowMemberCardForm(false);
            setEditingMemberIndex(null);
          }}
          onCancel={() => {
            setShowMemberCardForm(false);
            setEditingMemberIndex(null);
          }}
        />
      )}
    </div>
  );
}

export default OrganizationMembersManagement;